import { TextGradient } from "@components/utils/TextGradient";
import { useTimer } from "@hooks/useTimer";
import { cn, PropsWithClassName } from "kz-ui-sdk";
import moment, { Moment } from "moment";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

interface CountdownProps extends PropsWithClassName {
  className?: string;
  endTime?: Moment;
  title: string;
  autoStart?: boolean;
  classNameTitle?: string;
  onComplete?: () => void;
}

export interface CountdownRef {
  start: () => void;
  stop: () => void;
}

const Countdown = forwardRef<CountdownRef, CountdownProps>(
  ({ className, endTime, title, autoStart, classNameTitle, onComplete }, ref) => {
    const {
      timeRemaining,
      start: startTimer,
      stop: stopTimer,
      isRunning,
    } = useTimer({
      endTime: endTime ?? moment(),
      format: "HH:mm:ss",
      onComplete,
    });

    const prevEndTimeRef = useRef<number | null>(null);
    const endTimeValue = endTime?.valueOf() ?? null;

    useEffect(() => {
      if (endTimeValue === null) return;

      const prevEndTimeValue = prevEndTimeRef.current;

      // Start timer if not running and autoStart is enabled
      if (autoStart && !isRunning) {
        prevEndTimeRef.current = endTimeValue;
        startTimer();
        return;
      }

      // Restart timer if endTime actually changed (by timestamp value, not object reference)
      if (autoStart && isRunning && prevEndTimeValue !== null && prevEndTimeValue !== endTimeValue) {
        prevEndTimeRef.current = endTimeValue;
        stopTimer();
        // Use setTimeout to ensure stop completes before start
        setTimeout(() => {
          startTimer();
        }, 0);
      } else if (prevEndTimeValue === null) {
        prevEndTimeRef.current = endTimeValue;
      }
    }, [endTimeValue, autoStart, isRunning, startTimer, stopTimer]);

    useImperativeHandle(ref, () => ({
      start: startTimer,
      stop: stopTimer,
    }));

    const displayTime = endTime ? timeRemaining : "00:00:00";

    return (
      <div className={cn(className, "flex flex-col items-center space-y-1.5")}>
        <span
          className={cn(
            "text-sm font-semibold uppercase leading-[18px] tracking-[3px] text-content-soft",
            classNameTitle,
          )}
        >
          {title}
        </span>
        <TextGradient
          text={displayTime}
          className="h-[40px]"
          classes={{
            label: "text-4xl",
            labelOverlay: "text-4xl",
          }}
        />
      </div>
    );
  },
);

Countdown.displayName = "Countdown";

export default Countdown;
